/**
 * Playbook Manager — admin-only page for editing the Strategy v2 playbook.
 *
 * Route: /admin/strategy-playbook
 *
 * The playbook is the agency's strategic operating manual. It drives:
 *   - Stage 0 (Intake) — which archetype fires which channel mix
 *   - Stage 3 (Synthesis) — playbook context notes injected into the LLM prompt
 *   - Stage 3.5 (Cross-channel) — same context notes
 *
 * Every strategy build stamps `playbook_id` + `playbook_version` on the
 * strategy doc so reruns / historical exports use the exact rules in
 * effect at draft time. Editing here does NOT retroactively change past
 * strategies — only future builds pick up the change.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  BookOpenCheck, Loader2, Save, Plus, Trash2, CheckCircle2, GitBranch,
  Copy, AlertTriangle,
} from "lucide-react";

const ARCHETYPES = [
  { value: "rank_specific_page", label: "Rank a specific page" },
  { value: "build_topic_authority", label: "Build topic authority" },
  { value: "launch_new_service_line", label: "Launch a new service line" },
  { value: "defend_brand_serp", label: "Defend brand SERP" },
];

const CHANNEL_TYPES = [
  "seo", "blog", "instagram", "facebook", "linkedin", "tiktok", "x_twitter",
  "pinterest", "email", "paid_search", "paid_social", "video_ads",
  "ai_search_geo", "organic_social",
];

export default function StrategyPlaybookManagerPage() {
  const [playbooks, setPlaybooks] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/strategy-v2/playbooks");
      setPlaybooks(data.playbooks || []);
      const active = (data.playbooks || []).find((p) => p.active)
                    || (data.playbooks || [])[0];
      setCurrent(active ? structuredClone(active) : null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load playbooks");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isDirty = useMemo(() => {
    if (!current) return false;
    const stored = playbooks.find((p) => p.playbook_id === current.playbook_id);
    if (!stored) return true;
    return JSON.stringify(stored.rules) !== JSON.stringify(current.rules)
        || stored.context_notes !== current.context_notes
        || stored.name !== current.name;
  }, [current, playbooks]);

  const saveInPlace = async () => {
    if (!current || !isDirty) return;
    setSaving(true);
    try {
      await api.patch(`/strategy-v2/playbooks/${current.playbook_id}`, {
        name: current.name,
        context_notes: current.context_notes,
        rules: current.rules,
      });
      toast.success("Playbook updated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const saveAsNewVersion = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const { data } = await api.post("/strategy-v2/playbooks", {
        name: `${current.name} (${new Date().toISOString().slice(0, 10)})`,
        context_notes: current.context_notes,
        rules: current.rules,
        activate: true,
      });
      toast.success(`New version ${data.version} created & activated`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save-as-new failed");
    } finally { setSaving(false); }
  };

  const activate = async (playbook_id) => {
    try {
      await api.post(`/strategy-v2/playbooks/${playbook_id}/activate`);
      toast.success("Playbook activated");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Activate failed"); }
  };

  const removePlaybook = async (playbook_id) => {
    if (!window.confirm("Delete this playbook version? This cannot be undone.")) return;
    try {
      await api.delete(`/strategy-v2/playbooks/${playbook_id}`);
      toast.success("Playbook deleted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const addRule = () => {
    setCurrent({
      ...current,
      rules: [...current.rules, {
        id: `rule-${Date.now().toString(36)}`,
        objective_archetype: "rank_specific_page",
        recommend_channels: [{ type: "seo", role: "primary", priority: 1,
                               sequence: "structural → on-page → content" }],
        sequencing: ["seo"],
        reinforcement: [],
        source: "strategist-edit",
        source_tier: "expert",
        confidence: 0.8,
        status: "active",
      }],
    });
  };

  const updateRule = (idx, patch) => {
    const rules = current.rules.slice();
    rules[idx] = { ...rules[idx], ...patch };
    setCurrent({ ...current, rules });
  };

  const removeRule = (idx) => {
    setCurrent({ ...current, rules: current.rules.filter((_, i) => i !== idx) });
  };

  if (loading) {
    return (
      <div className="p-6"><Card className="rounded-sm">
        <CardContent className="p-6 flex items-center gap-2 text-sm text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading playbooks…
        </CardContent></Card></div>
    );
  }

  if (!current) {
    return (
      <div className="p-6"><Card className="rounded-sm border-dashed">
        <CardContent className="p-10 text-center text-sm text-zinc-500">
          <BookOpenCheck className="h-6 w-6 mx-auto mb-2 text-zinc-300" />
          No playbook found. Something&apos;s wrong — the starter should have seeded automatically.
        </CardContent></Card></div>
    );
  }

  return (
    <div className="p-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4"
      data-testid="playbook-manager-page">
      {/* Sidebar: version list */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <BookOpenCheck className="h-4 w-4 text-black" />
          <h2 className="font-display font-bold text-base">Playbook Versions</h2>
        </div>
        <div className="space-y-1.5" data-testid="playbook-versions-list">
          {playbooks.map((p) => (
            <div key={p.playbook_id}
              className={`border rounded-sm p-2.5 cursor-pointer transition-colors ${
                current.playbook_id === p.playbook_id
                  ? "border-black bg-zinc-50" : "border-zinc-200 hover:border-zinc-400"}`}
              onClick={() => setCurrent(structuredClone(p))}
              data-testid={`playbook-version-${p.playbook_id}`}>
              <div className="flex items-center gap-2">
                <span className="text-[12px] font-semibold truncate flex-1">{p.name}</span>
                {p.active && <Badge variant="outline" className="rounded-sm text-[8px] bg-black text-white border-black">ACTIVE</Badge>}
              </div>
              <div className="text-[10px] font-mono text-zinc-500 mt-0.5 truncate">
                {p.version} · {p.rules?.length || 0} rules
              </div>
              <div className="flex items-center gap-1 mt-1.5">
                {!p.active && (
                  <Button size="sm" variant="outline" className="rounded-sm h-6 text-[10px] flex-1 border-black"
                    onClick={(e) => { e.stopPropagation(); activate(p.playbook_id); }}
                    data-testid={`playbook-activate-${p.playbook_id}`}>
                    Activate
                  </Button>
                )}
                {!p.active && (
                  <Button size="sm" variant="ghost" className="rounded-sm h-6 w-6 p-0 text-red-600"
                    onClick={(e) => { e.stopPropagation(); removePlaybook(p.playbook_id); }}
                    data-testid={`playbook-delete-${p.playbook_id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Editor */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="font-display font-bold text-xl flex-1">
            {current.active ? "Active playbook" : "Historical playbook (read for edit)"}
          </h1>
          <Badge variant="outline" className="rounded-sm text-[10px] font-mono">{current.version}</Badge>
          {isDirty && <Badge variant="outline" className="rounded-sm text-[10px] bg-amber-50 text-amber-800 border-amber-300">UNSAVED</Badge>}
        </div>

        <Card className="rounded-sm"><CardContent className="p-4 space-y-3">
          <div>
            <Label className="text-[11px] font-mono uppercase text-zinc-500">Playbook name</Label>
            <Input value={current.name || ""}
              onChange={(e) => setCurrent({ ...current, name: e.target.value })}
              data-testid="playbook-name-input" />
          </div>
          <div>
            <Label className="text-[11px] font-mono uppercase text-zinc-500">Context notes (§2.2 free-text guidance)</Label>
            <p className="text-[10px] text-zinc-500 mb-1">
              Injected verbatim into every Stage 3 (Synthesis) and Stage 3.5 (Cross-channel) prompt. Use it for nuance the rules can&apos;t capture — algo updates, seasonal advice, one-off constraints.
            </p>
            <Textarea rows={6} value={current.context_notes || ""}
              onChange={(e) => setCurrent({ ...current, context_notes: e.target.value })}
              className="text-[12px]"
              data-testid="playbook-context-notes-input" />
          </div>
        </CardContent></Card>

        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="font-display font-bold text-base flex-1">Rules ({current.rules?.length || 0})</h2>
          <Button size="sm" variant="outline" className="rounded-sm h-8 border-black"
            onClick={addRule} data-testid="playbook-add-rule">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add rule
          </Button>
        </div>

        <div className="space-y-2" data-testid="playbook-rules-list">
          {(current.rules || []).map((rule, idx) => (
            <RuleEditor key={rule.id || idx} rule={rule} idx={idx}
              onChange={(patch) => updateRule(idx, patch)}
              onRemove={() => removeRule(idx)} />
          ))}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-zinc-200 py-3 flex items-center gap-2 justify-end">
          {!current.active && (
            <div className="flex items-center gap-1.5 text-[11px] text-amber-800 mr-auto">
              <AlertTriangle className="h-3.5 w-3.5" />
              Editing a non-active version. Save-as-new to publish.
            </div>
          )}
          <Button size="sm" variant="outline" className="rounded-sm h-8 border-black"
            onClick={saveAsNewVersion} disabled={saving}
            data-testid="playbook-save-as-new">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <GitBranch className="h-3.5 w-3.5 mr-1" />}
            Save as new version & activate
          </Button>
          {current.active && (
            <Button size="sm" className="rounded-sm h-8 bg-black hover:bg-zinc-800 text-white"
              onClick={saveInPlace} disabled={saving || !isDirty}
              data-testid="playbook-save-inplace">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
              Save changes
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================== RULE EDITOR ============================== */

function RuleEditor({ rule, idx, onChange, onRemove }) {
  const channels = rule.recommend_channels || [];
  const setChannel = (cidx, patch) => {
    const next = channels.slice();
    next[cidx] = { ...next[cidx], ...patch };
    onChange({ recommend_channels: next });
  };
  const addChannel = () => onChange({
    recommend_channels: [...channels, {
      type: "seo", role: "amplification",
      priority: channels.length + 1, sequence: "",
    }],
  });
  const removeChannel = (cidx) => onChange({
    recommend_channels: channels.filter((_, i) => i !== cidx),
  });

  return (
    <div className="border border-zinc-300 rounded-sm p-3 space-y-3"
      data-testid={`playbook-rule-${idx}`}>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase text-zinc-500">Rule {idx + 1}</span>
        <Input value={rule.id || ""} onChange={(e) => onChange({ id: e.target.value })}
          className="flex-1 max-w-[300px] font-mono text-[11px] h-7"
          placeholder="rule-id-slug" data-testid={`playbook-rule-${idx}-id`} />
        <select value={rule.status || "active"}
          onChange={(e) => onChange({ status: e.target.value })}
          className="border border-zinc-300 rounded-sm text-[11px] h-7 px-1 font-mono">
          <option value="active">active</option>
          <option value="draft">draft</option>
          <option value="archived">archived</option>
        </select>
        <Button size="sm" variant="ghost" className="rounded-sm h-7 w-7 p-0 text-red-600"
          onClick={onRemove} data-testid={`playbook-rule-${idx}-remove`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          <Label className="text-[10px] font-mono uppercase text-zinc-500">Objective archetype</Label>
          <select value={rule.objective_archetype || ""}
            onChange={(e) => onChange({ objective_archetype: e.target.value })}
            className="w-full border border-zinc-300 rounded-sm text-[12px] h-8 px-1.5"
            data-testid={`playbook-rule-${idx}-archetype`}>
            {ARCHETYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase text-zinc-500">Confidence (0-1)</Label>
          <Input type="number" step="0.05" min="0" max="1"
            value={rule.confidence ?? 0.8}
            onChange={(e) => onChange({ confidence: parseFloat(e.target.value) || 0 })}
            className="h-8 text-[12px]" />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase text-zinc-500">Source tier</Label>
          <select value={rule.source_tier || "expert"}
            onChange={(e) => onChange({ source_tier: e.target.value })}
            className="w-full border border-zinc-300 rounded-sm text-[12px] h-8 px-1.5">
            <option value="expert">expert</option>
            <option value="research">research</option>
            <option value="client-specific">client-specific</option>
            <option value="experimental">experimental</option>
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Label className="text-[10px] font-mono uppercase text-zinc-500 flex-1">Recommended channels ({channels.length})</Label>
          <Button size="sm" variant="ghost" className="rounded-sm h-6 text-[10px]"
            onClick={addChannel}>
            <Plus className="h-3 w-3 mr-1" /> Add channel
          </Button>
        </div>
        <div className="space-y-1.5">
          {channels.map((c, cidx) => (
            <div key={cidx} className="grid grid-cols-[130px_130px_60px_1fr_28px] gap-1.5 items-center">
              <select value={c.type} onChange={(e) => setChannel(cidx, { type: e.target.value })}
                className="border border-zinc-300 rounded-sm text-[11px] h-7 px-1 font-mono">
                {CHANNEL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <Input value={c.role || ""} placeholder="role"
                onChange={(e) => setChannel(cidx, { role: e.target.value })}
                className="h-7 text-[11px]" />
              <Input type="number" value={c.priority ?? 1}
                onChange={(e) => setChannel(cidx, { priority: parseInt(e.target.value, 10) || 1 })}
                className="h-7 text-[11px] font-mono" />
              <Input value={c.sequence || ""} placeholder="sequence hint"
                onChange={(e) => setChannel(cidx, { sequence: e.target.value })}
                className="h-7 text-[11px]" />
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600"
                onClick={() => removeChannel(cidx)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-[10px] font-mono uppercase text-zinc-500">Reinforcement rules (one per line)</Label>
        <Textarea rows={3}
          value={(rule.reinforcement || []).join("\n")}
          onChange={(e) => onChange({ reinforcement:
            e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
          className="text-[11px] font-mono"
          placeholder="e.g. fix branded SERP defence before scaling non-branded paid spend" />
      </div>
    </div>
  );
}
